import { useEffect, useMemo, useState } from "preact/hooks";
import { closeConversation, fetchDashboard } from "../api";
import type { DashboardConversationRow, DashboardData } from "../types";
import {
  addSaoPauloCalendarDays,
  fmtCanonicalPt,
  fmtDateTime,
  fmtDisplayName,
  fmtPhoneDisplay,
  fmtSaoPauloDateInput,
} from "../format";
import { AlertIconBadge, Badge, PriorityBadge, StateBadge, StateIconBadge } from "../components/atoms/Badge";
import { AppShell } from "../components/AppShell";
import { crmFinalLabel, EgoLink, getCrmFinalTarget } from "../components/CrmLinks";
import { Pagination, usePagination } from "../components/Pagination";
import { useDebouncedSearch } from "../hooks/useDebouncedSearch";

interface FiltersState {
  q: string;
  intent: string;
  classification: string;
  dateFrom: string;
  dateTo: string;
}

function buildDefaultFilters(): FiltersState {
  const today = fmtSaoPauloDateInput();
  return {
    q: "",
    intent: "",
    classification: "",
    dateFrom: addSaoPauloCalendarDays(today, -3),
    dateTo: today,
  };
}

function sameMockSuffix(): string {
  return new URLSearchParams(window.location.search).get("mock") === "1" ? "?mock=1" : "";
}

function rowDate(row: DashboardConversationRow): number {
  const value = row.updated_at || row.created_at;
  if (!value) return 0;
  const n = new Date(value).getTime();
  return Number.isFinite(n) ? n : 0;
}

function matchesFilters(row: DashboardConversationRow, filters: FiltersState): boolean {
  const q = filters.q.trim().toLowerCase();
  if (q) {
    const haystack = [
      row.client_name,
      row.client_phone,
      fmtPhoneDisplay(row.client_phone),
      row.client_email,
    ].filter(Boolean).join(" ").toLowerCase();
    if (!haystack.includes(q)) return false;
  }
  if (filters.intent && row.intent !== filters.intent) return false;
  if (filters.classification && row.classification !== filters.classification) return false;
  const d = rowDate(row);
  if (filters.dateFrom) {
    const from = new Date(`${filters.dateFrom}T00:00:00-03:00`).getTime();
    if (d < from) return false;
  }
  if (filters.dateTo) {
    const to = new Date(`${filters.dateTo}T23:59:59-03:00`).getTime();
    if (d > to) return false;
  }
  return true;
}

export function DashboardPage({ mock }: { mock: boolean }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [filters, setFilters] = useState<FiltersState>(() => buildDefaultFilters());
  const { searchValue, setSearchValue, debouncedQuery } = useDebouncedSearch(filters.q);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;
  const effectiveFilters = useMemo<FiltersState>(() => ({
    ...filters,
    q: debouncedQuery,
  }), [filters, debouncedQuery]);
  const filterKey = JSON.stringify(effectiveFilters);

  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchDashboard(mock, mock ? {} : {
      q: effectiveFilters.q,
      intent: effectiveFilters.intent,
      classification: effectiveFilters.classification,
      date_from: effectiveFilters.dateFrom,
      date_to: effectiveFilters.dateTo,
      page,
      page_size: pageSize,
    })
      .then((d) => {
        if (!d || d.ok === false) setError(d?.error || "Dashboard indisponível.");
        else {
          setData(d);
          setSelectedId(d.conversations[0]?.id || null);
        }
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [mock, filterKey, page, refreshKey]);

  const rows = useMemo(
    () => {
      const sourceRows = data?.conversations || [];
      return mock ? sourceRows.filter((row) => matchesFilters(row, effectiveFilters)) : sourceRows;
    },
    [data, filterKey, mock],
  );
  const localPagination = usePagination(rows, {
    pageSize,
    resetKey: filterKey,
  });
  const pageItems = mock ? localPagination.pageItems : rows;
  const serverTotal = data?.pagination?.total_items ?? rows.length;
  const serverPageCount = Math.max(1, data?.pagination?.page_count ?? 1);
  const serverRangeStart = serverTotal ? (page - 1) * pageSize + 1 : 0;
  const serverRangeEnd = Math.min(serverTotal, (page - 1) * pageSize + rows.length);
  const selected = pageItems.find((row) => row.id === selectedId)
    || pageItems[0]
    || null;

  const subtitle = data
    ? `Atualizado em ${fmtDateTime(data.generated_at)} GMT-3`
    : null;

  const handleClose = async (conversationId: string) => {
    if (mock || closingId) return;
    setClosingId(conversationId);
    setError(null);
    try {
      const result = await closeConversation(conversationId);
      if (!result.ok) throw new Error(result.error || "Falha ao fechar conversa.");
      setRefreshKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setClosingId(null);
    }
  };

  if (loading && !data) {
    return (
      <AppShell active="conversas" title="Conversas" mock={mock}>
        <div class="state-msg">Carregando dashboard…</div>
      </AppShell>
    );
  }
  if ((error && !data) || !data) {
    return (
      <AppShell active="conversas" title="Conversas" mock={mock}>
        <div class="state-error">{error}</div>
      </AppShell>
    );
  }

  return (
    <AppShell
      active="conversas"
      title="Conversas"
      subtitle={subtitle}
      mock={mock}
      inspector={<ConversationInspector row={selected} mock={mock} />}
      headerExtra={
        <section class="filter-bar filter-bar--header">
          <label class="field field--search">
            <span>Busca</span>
            <input
              value={searchValue}
              onInput={(e) => setSearchValue(e.currentTarget.value)}
              placeholder="Nome ou telefone"
            />
          </label>
          <label class="field">
            <span>Intent</span>
            <select
              value={filters.intent}
              onChange={(e) => setFilters({ ...filters, intent: e.currentTarget.value })}
            >
              <option value="">Todos</option>
              {data.filters.intents.map((intent) => (
                <option value={intent} key={intent}>{fmtCanonicalPt(intent)}</option>
              ))}
            </select>
          </label>
          <label class="field">
            <span>Classificação</span>
            <select
              value={filters.classification}
              onChange={(e) => setFilters({ ...filters, classification: e.currentTarget.value })}
            >
              <option value="">Todas</option>
              {data.filters.classifications.map((c) => <option value={c} key={c}>{c}</option>)}
            </select>
          </label>
          <label class="field">
            <span>De</span>
            <input
              type="date"
              value={filters.dateFrom}
              onInput={(e) => setFilters({ ...filters, dateFrom: e.currentTarget.value })}
            />
          </label>
          <label class="field">
            <span>Até</span>
            <input
              type="date"
              value={filters.dateTo}
              onInput={(e) => setFilters({ ...filters, dateTo: e.currentTarget.value })}
            />
          </label>
        </section>
      }
      bodyTop={
        <section class="metric-grid metric-grid--singleline">
          {data.metrics.map((m) => (
            <div class="metric-card" key={m.key}>
              <div class="metric-value">{m.value}</div>
              <div class="metric-label">{m.label}</div>
            </div>
          ))}
        </section>
      }
    >
        <section class="rows-panel">
          <div class="rows-panel-head">
            <span>{rows.length} conversas</span>
            <span class="rows-panel-actions">
              {mock && <Badge variant="info">mock</Badge>}
            </span>
          </div>
          <div class="rows-panel-scroll">
            {loading && <div class="state-msg">Atualizando conversas…</div>}
            {pageItems.map((row) => (
              <ConversationRow
                row={row}
                selected={selected?.id === row.id}
                onSelect={() => setSelectedId(row.id)}
                onClose={() => handleClose(row.id)}
                closeDisabled={mock || closingId === row.id}
                key={row.id}
              />
            ))}
          </div>
          <Pagination
            page={mock ? localPagination.page : page}
            pageCount={mock ? localPagination.pageCount : serverPageCount}
            rangeStart={mock ? localPagination.rangeStart : serverRangeStart}
            rangeEnd={mock ? localPagination.rangeEnd : serverRangeEnd}
            totalItems={mock ? localPagination.totalItems : serverTotal}
            onPageChange={mock ? localPagination.setPage : setPage}
          />
        </section>
    </AppShell>
  );
}

function ConversationRow(
  { row, selected, onSelect, onClose, closeDisabled }: {
    row: DashboardConversationRow;
    selected: boolean;
    onSelect: () => void;
    onClose: () => void;
    closeDisabled: boolean;
  },
) {
  const suffix = sameMockSuffix();
  const selectFromRow = (event: Event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest("a, button")) return;
    onSelect();
  };
  const selectFromKeyboard = (event: KeyboardEvent) => {
    if (event.currentTarget !== event.target) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onSelect();
  };
  return (
    <article
      class={`conv-row conv-row--selectable ${selected ? "is-selected" : ""}`}
      role="button"
      tabIndex={0}
      onClick={selectFromRow}
      onKeyDown={selectFromKeyboard}
      aria-pressed={selected ? "true" : "false"}
    >
      <div class="conv-row-main">
        <div class="conv-row-title">
          <a href={`/clientes/${row.session_id}${suffix}`}>{fmtDisplayName(row.client_name)}</a>
          <span>{fmtPhoneDisplay(row.client_phone)}</span>
        </div>
        <div class="conv-row-meta">
          <span class="conv-row-intent">{fmtCanonicalPt(row.intent || "") || "Sem intent"}</span>
          <span>{fmtDateTime(row.updated_at)}</span>
          <span>{row.msg_count || 0} msgs</span>
          <span>{row.match_count || 0} matches</span>
        </div>
      </div>
      <div class="conv-row-badges">
        <PriorityBadge priority={row.classification as never} />
        <StateIconBadge state={row.terminal_state} closeReason={row.close_reason} />
        <AlertIconBadge count={row.alert_count} alerts={row.alerts} />
      </div>
      <div class="conv-row-actions">
        {!row.closed && (
          <button
            class="btn btn--icon btn--danger-icon"
            type="button"
            disabled={closeDisabled}
            onClick={onClose}
            aria-label="Fechar conversa"
            title="Fechar conversa"
          >
            <CloseConversationIcon />
          </button>
        )}
        <a class="btn" href={`/conv/${row.id}${suffix}`}>Abrir</a>
      </div>
    </article>
  );
}

function CloseConversationIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm0 2a8 8 0 1 1 0 16 8 8 0 0 1 0-16Zm-3.3 3.3L12 10.6l3.3-3.3 1.4 1.4L13.4 12l3.3 3.3-1.4 1.4L12 13.4l-3.3 3.3-1.4-1.4 3.3-3.3-3.3-3.3 1.4-1.4Z" />
    </svg>
  );
}

function ConversationInspector(
  { row, mock }: { row?: DashboardConversationRow | null; mock: boolean },
) {
  const suffix = mock ? "?mock=1" : "";
  if (!row) {
    return <div class="inspector-card"><div class="state-msg">Sem conversa selecionada.</div></div>;
  }
  const events = [
    ["Criada", row.created_at],
    ["Atualizada", row.updated_at],
    ["Lead enviado", row.lead_sent_at],
  ].filter(([, value]) => !!value);
  const crmFinal = getCrmFinalTarget(
    row.crm_final_emission_kind,
    row.ego_opportunity_id,
    row.ego_lead_id,
    row.ego_opportunity_url,
    row.ego_lead_url,
  );
  return (
    <div class="inspector-stack">
      <section class="inspector-card">
        <h2>Resumo</h2>
        <div class="inspector-title">{fmtDisplayName(row.client_name)}</div>
        <div class="inspector-muted">{fmtPhoneDisplay(row.client_phone)}</div>
        <div class="inspector-badges">
          <PriorityBadge priority={row.classification as never} />
          <StateBadge state={row.terminal_state} />
        </div>
        <dl class="inspector-kv">
          <dt>Intent</dt>
          <dd>{fmtCanonicalPt(row.intent || "") || "—"}</dd>
          <dt>Entity eGO</dt>
          <dd>
            <EgoLink
              id={row.ego_entity_id}
              label="Entity"
              kind="person"
            />
          </dd>
          <dt>Tipo CRM final</dt>
          <dd>{row.crm_final_emission_kind ? crmFinalLabel(row.crm_final_emission_kind, true) : "—"}</dd>
          <dt>{crmFinalLabel(row.crm_final_emission_kind)}</dt>
          <dd>
            <EgoLink
              id={crmFinal.id}
              label={crmFinal.label}
              kind="lead"
              url={crmFinal.url}
            />
          </dd>
          <dt>Mensagens</dt>
          <dd>{row.msg_count || 0}</dd>
          <dt>Matches</dt>
          <dd>{row.match_count || 0}</dd>
        </dl>
        <a class="btn inspector-button" href={`/conv/${row.id}${suffix}`}>Abrir conversa</a>
      </section>
      <section class="inspector-card">
        <h2>Alertas</h2>
        {row.alerts?.length
          ? row.alerts.map((alert) => (
            <div class="inspector-alert" key={`${alert.label}-${alert.detail}`}>
              <Badge variant={alert.level === "danger" ? "danger" : alert.level === "warn" ? "warn" : "info"}>
                {alert.label}
              </Badge>
              {alert.detail && <span>{alert.detail}</span>}
            </div>
          ))
          : <div class="inspector-muted">Sem alertas.</div>}
      </section>
      <section class="inspector-card">
        <h2>Últimos eventos</h2>
        <div class="event-list">
          {events.map(([label, value]) => (
            <div class="event-row" key={label}>
              <span>{label}</span>
              <strong>{fmtDateTime(value)}</strong>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
